// i18next-extract-mark-ns-start FAQ
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import checkZipcodeError from 'tilli-core/dist/services/zipcode';
import { mondialRelayShippingMethods, colissimoShippingMethods } from 'tilli-core/dist/services/sendcloudData';
import { withTranslation } from 'gatsby-plugin-react-i18next';

import withAppContext from '../../withAppContext';
import withOrderContext from '../../withOrderContext';
import { colors, tabletThresholdPixels } from './v4/styledComponents';
import { FormInput as StyledFormInput, Button as StyledButton } from './v2/styledComponents';
import downIcon from '../../assets/faq/downIcon.svg';
import upIcon from '../../assets/faq/upIcon.svg';
import downIconSmall from '../../assets/faq/downIconSmall.svg';
import upIconSmall from '../../assets/faq/upIconSmall.svg';
import errorLogo from '../../assets/error.png';
import { renderGatsbyLink } from './faqComponents';
import { formatPrice } from '../../services/pricing';

const Container = styled.div`
  position: relative;
  ${({ hasLeftPosition }) => hasLeftPosition && 'left: 29px;'}
  outline: none;

  @media (max-width: ${tabletThresholdPixels}) {
    left: ${({ alwaysOpened }) => alwaysOpened ? '0' : '29'}px;
    padding-right: ${({ alwaysOpened }) => alwaysOpened ? '0' : '29'}px;
  }
`;

const QuestionContainer = styled.div`
  cursor: ${({ alwaysOpened }) => alwaysOpened ? 'default' : 'pointer'};
`;

const QuestionTitle = styled.h4`
  color: ${colors.black};
  font-size: 16px;
  margin-top: ${({ isOpened, noTransition }) => isOpened && !noTransition ? '52' : '36'}px;
  ${({ noTransition }) => !noTransition && 'transition: margin-top 0.2s ease-in-out;'}
  font-weight: 500;
  margin-bottom: ${({ isOpened }) => isOpened ? '10' : '36'}px;
`;

const Answer = styled.p`
  color: ${colors.black};
  font-size: 16px;
  margin: 0px;
  &:last-of-type {
    ${({ hasMarginBottom }) => hasMarginBottom && 'margin-bottom: 36px;'}
  }
  ${({ isLandingPage }) => isLandingPage && 'font-size: 12px;'}
  @media (max-width: ${tabletThresholdPixels}) {
    ${({ isLandingPage }) => isLandingPage ? 'font-size: 12px;' : 'font-size: 14px;'}
  }
`;

const Icon = styled.img`
  position: absolute;
  left: -29px;
  top: 7px;
`;

const Bold = styled.span`
  font-weight: 500;
  cursor: pointer;
  font-size: 16px;
  @media (max-width: ${tabletThresholdPixels}) {
    font-size: 14px;
    line-height: 21px;
  }
`;

const FormInputContainer = styled.div`
  display: flex;
  margin-top: 20px;
  margin-bottom: 40px;
  align-items: center;
  position: relative;
  @media (max-width: ${tabletThresholdPixels}) {
    font-size: 16px;
    justify-content: space-between;
    width: 100%;
    max-width: 400px;
    margin-top: 10px;
    margin-bottom: 20px;
  }
`;

const FormInput = styled(StyledFormInput)`
  width: 226px;
  margin: 0px;
  padding-top: 0px;
  padding-bottom: 0px;
  height: 38px;
  font-size: 16px;
  &::placeholder {
    color: ${colors.grey504};
    font-size: 14px;
  }
  &:focus {
    outline: none;
  }
  @media (max-width: ${tabletThresholdPixels}) {
    margin: 0px;
    width: 45%;
  }
`;

const Button = styled(StyledButton)`
  margin: 0px 0px 0px 10px;
  height: 40px;
  min-height: unset;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (min-width: ${tabletThresholdPixels}) {
    &:hover {
      transform: none;
    }
  }

  @media (max-width: ${tabletThresholdPixels}) {
    margin: 0px 0px 0px 10px;
    height: 40px;
    min-height: unset;
    min-width: unset;
    width: 45%;
  }
`;

const ResponseText = styled.p`
  font-family: Roboto;
  font-size: 14px;
  font-weight: 700;
  line-height: 16px;
  letter-spacing: 0em;
  text-align: left;
  color: ${colors.black};
  position: absolute;
  margin: 0px;
  top: 75px;
`;

const ErrorText = styled(ResponseText)`
  color: ${colors.red500};
`;

const FormAndMessageContainer = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  align-items: flex-start;
`;

const I = styled.i`
  background-image: url(${errorLogo});
  background-size: contain;
  width: 10px;
  height: 10px;
  position: absolute;
  left: 225px;
  @media (max-width: ${tabletThresholdPixels}) {
    left: 45%;
  }
`;

const FormattedSpan = styled.span`
  ${({ isBold }) => isBold && 'font-weight: 500;'}
  ${({ isItalic }) => isItalic && 'font-style: italic;'}
`;

const Table = styled.table`
  margin-top: 15px;
  border-collapse: collapse;
  border: 1px solid ${colors.grey200};
  font-size: 16px;
  ${({ isLandingPage }) => isLandingPage && 'font-size: 12px;'}
  @media (max-width: ${tabletThresholdPixels}) {
    ${({ isLandingPage }) => isLandingPage ? 'font-size: 12px;' : 'font-size: 14px;'}
  }
`;

const Td = styled.td`
  border: 1px solid ${colors.grey200};
  padding: 10px 20px;
`;

const Th = styled.th`
  border: 1px solid ${colors.grey200};
  padding: 10px 20px;
  background-color: ${colors.grey100};
  font-weight: 500;
  text-align: start;
`;

/* eslint-disable react/no-array-index-key */
const toFormattedItalicText = (text) => {
  const splitText = text.split('<italic>').filter((line) => line !== '');
  if (text.indexOf('<italic>') === 0) {
    return splitText.map((line, index) => (
      <FormattedSpan key={`${line}${index}`} isItalic={index % 2 === 0}>
        {line}
      </FormattedSpan>
    ));
  }
  return splitText.map((line, index) => (
    <FormattedSpan key={`${line}${index}`} isItalic={index % 2 !== 0}>
      {line}
    </FormattedSpan>
  ));
};

export const toFormattedBoldAndItalicText = (text) => {
  const splitText = text.split('<bold>').filter((line) => line !== '');
  if (text.indexOf('<bold>') === 0) {
    return splitText.map((line, index) => (
      <FormattedSpan key={`${line}${index}`} isBold={index % 2 === 0}>
        {toFormattedItalicText(line)}
      </FormattedSpan>
    ));
  }
  return splitText.map((line, index) => (
    <FormattedSpan key={`${line}${index}`} isBold={index % 2 !== 0}>
      {toFormattedItalicText(line)}
    </FormattedSpan>
  ));
};

const getPriceByWeight = (weight, prices) => {
  const weightInKg = weight / 1000;
  const priceObj = prices.find((p) => weightInKg > p.minWeight && weightInKg <= p.maxWeight) || prices[prices.length - 1];
  return priceObj.FR;
};

export const renderPackagePrice = (t, currency, isLandingPage) => {
  const weights = [250, 500, 1000, 2000, 3000, 4000, 5000, 6000, 7000];
  const sendcloudExtraLabelFee = 0.1;

  return (
    <>
      <br />
      <Bold>{t('Notre grille tarifaire pour 1 colis - France')}</Bold>
      <Table isLandingPage={isLandingPage}>
        <tr>
          <Th>{t('Poids max')}</Th>
          <Th>{t('Mondial Relay')}</Th>
          <Th>{t('Colissimo')}</Th>
        </tr>
        <tr>
          <Th>{t('Délais')}</Th>
          <Td>{t('J+3*')}</Td>
          <Td>{t('J+2*')}</Td>
        </tr>
        {weights.map((weight) => (
          <tr key={weight}>
            <Th>
              {weight / 1000}
              kg
            </Th>
            <Td>
              {formatPrice((getPriceByWeight(weight, mondialRelayShippingMethods) + sendcloudExtraLabelFee), currency)}
            </Td>
            <Td>
              {formatPrice((getPriceByWeight(weight, colissimoShippingMethods) + sendcloudExtraLabelFee), currency)}
            </Td>
          </tr>
        ))}
      </Table>
      <Answer isLandingPage={isLandingPage}>{t('* jours ouvrés à partir du dépôt en point relais ou en bureau de poste')}</Answer>
    </>
  );
};

export const renderColissimoInsurance = (t, isLandingPage = false) => (
  <Answer isLandingPage={isLandingPage}>
    <u>{t('COLISSIMO')}</u>
    <Table isLandingPage={isLandingPage}>
      <tr>
        <Th>{t('Indemnisation forfaitaire TTC')}</Th>
        <Th>{t('Montant assurance TTC')}</Th>
      </tr>
      <tr>
        <Th>150€</Th>
        <Td>1,20€</Td>
      </tr>
      <tr>
        <Th>300€</Th>
        <Td>2,40€</Td>
      </tr>
      <tr>
        <Th>500€</Th>
        <Td>3,60€</Td>
      </tr>
      <tr>
        <Th>1000€</Th>
        <Td>7,20€</Td>
      </tr>
      <tr>
        <Th>2000€</Th>
        <Td>14,40€</Td>
      </tr>
      <tr>
        <Th>5000€</Th>
        <Td>36€</Td>
      </tr>
    </Table>
    <br />
    {t('En optant pour cette assurance, votre Colissimo est remis à son destinataire en mains propres et contre signature.')}
  </Answer>
);

export const renderMondialRelayInsurance = (t, isLandingPage = false) => (
  <>
    <br />
    <Answer isLandingPage={isLandingPage}>
      <u>{t('MONDIAL RELAY')}</u>
      <br />
      <Table isLandingPage={isLandingPage}>
        <tr>
          <Th>{t('Indemnisation forfaitaire TTC')}</Th>
          <Th>{t('Montant assurance TTC')}</Th>
        </tr>
        <tr>
          <Th>250€</Th>
          <Td>5€</Td>
        </tr>
        <tr>
          <Th>500€</Th>
          <Td>8€</Td>
        </tr>
      </Table>
      <br />
      {t('Ces niveaux d‘indemnisation forfaitaire garantissent la perte ou l‘avarie constatée* lors de l‘acheminement de votre colis (frais de port compris).')}
      <br />
      <i>{t('*Sous réserve d‘emballage conforme comme décrit ')}</i>
      {renderGatsbyLink('https://www.mondialrelay.fr/solutionspro/offres-et-services/offre-start/preparation-de-votre-colis/', t('ici'))}
      <br />
    </Answer>
    <br />
  </>
);
class Question extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isOpened: props.alwaysOpened,
    };
    this.toggle = this.toggle.bind(this);
  }

  toggle = () => {
    const { alwaysOpened } = this.props;
    const { isOpened } = this.state;
    if (alwaysOpened) return;
    this.setState({ isOpened: !isOpened });
  };

  checkZipcode = () => {
    const { zipcode } = this.state;
    const errorCode = checkZipcodeError(zipcode).code;
    this.setState({
      isOk: errorCode === 0,
      formatError: errorCode === 1,
      notDeliveredError: errorCode === 2,
    });
  };

  renderZipCodeQuestion = (answer) => {
    const { t } = this.props;
    const {
      isOk, formatError, notDeliveredError, zipcode,
    } = this.state;
    return (
      <React.Fragment key={answer}>
        <Answer key={answer}>
          {t('- Vous voulez savoir si Tilli se déplace chez vous ? ')}
          <Bold>
            {t('Testez votre code postal :')}
          </Bold>
        </Answer>
        <FormAndMessageContainer>
          <FormInputContainer>
            <FormInput
              name="zipcode"
              type="numeric"
              placeholder={t('Ex: 91344')}
              onChange={(e) => this.setState({ zipcode: e.target.value })}
              value={zipcode || ''}
            />
            {formatError === true && <I />}
            <Button onClick={this.checkZipcode} black>{t('Valider')}</Button>
          </FormInputContainer>
          {isOk
            && <ResponseText>{t('Nos Tillistes peuvent venir jusqu’à chez vous ;)')}</ResponseText>}
          {formatError && <ErrorText>{t('Code postal non valide')}</ErrorText>}
          {notDeliveredError
            && <ErrorText>{t('Nos Tillistes ne peuvent pas encore venir chez vous :(')}</ErrorText>}
        </FormAndMessageContainer>
      </React.Fragment>
    );
  };

  render() {
    const {
      question, alwaysOpened, context: { isMobile }, marginBottom, noTransition, hasBeginHyphen, hasLeftPosition, t,
      orderContext: { currency },
    } = this.props;
    const { isOpened } = this.state;
    return (
      <Container key={question.question} hasLeftPosition={hasLeftPosition} alwaysOpened={alwaysOpened}>
        <QuestionContainer onClick={this.toggle} alwaysOpened={alwaysOpened}>
          {!alwaysOpened && !isOpened && <Icon src={isMobile ? downIconSmall : downIcon} />}
          {!alwaysOpened && isOpened && <Icon src={isMobile ? upIconSmall : upIcon} />}
          <QuestionTitle isOpened={isOpened} noTransition={noTransition}>{question.question}</QuestionTitle>
        </QuestionContainer>
        {isOpened
          && question.answers.map((answer) => {
            if (!answer) return <br />;
            if (answer === 'ZIPCODE') return this.renderZipCodeQuestion(answer);
            if (answer === 'PACKAGE_DELIVERY_FEES') return renderPackagePrice(t, currency);
            if (answer === 'COLISSIMO_INSURANCE') return renderColissimoInsurance(t);
            if (answer === 'MONDIAL_RELAY_INSURANCE') return renderMondialRelayInsurance(t);
            return (
              <Answer key={answer} hasMarginBottom={marginBottom}>
                {typeof answer === 'object'
                  ? answer
                  : toFormattedBoldAndItalicText(`${(question.answers.length !== 1 && hasBeginHyphen) ? '- ' : ''}${answer}`)}
              </Answer>
            );
          })}
      </Container>
    );
  }
}

Question.propTypes = {
  question: PropTypes.shape({
    question: PropTypes.string,
    answers: PropTypes.arrayOf(PropTypes.string),
    links: PropTypes.arrayOf(PropTypes.node),
  }).isRequired,
  alwaysOpened: PropTypes.bool,
  context: PropTypes.shape({
    isMobile: PropTypes.bool,
  }).isRequired,
  marginBottom: PropTypes.bool,
  noTransition: PropTypes.bool,
  hasBeginHyphen: PropTypes.bool,
  hasLeftPosition: PropTypes.bool,
  t: PropTypes.func.isRequired,
  fromHowItWorks: PropTypes.bool,
  orderContext: PropTypes.shape({
    currency: PropTypes.string,
  }).isRequired,
};

Question.defaultProps = {
  alwaysOpened: false,
  marginBottom: false,
  noTransition: false,
  hasBeginHyphen: true,
  hasLeftPosition: true,
  fromHowItWorks: false,
};

export default withTranslation()(withAppContext(withOrderContext(Question)));
